<script setup lang="ts">
import { computed, useTemplateRef } from "vue";
import { UiButton, UiFormBuilder } from "@shopexperts/shared";
import type { BuilderRowConfig } from "@shopexperts/shared";
import {
    satelliteSiteFormSchema,
    type SatelliteSiteFormValues,
} from "@/app/composables/satellite-contact-sites/satelliteSiteForm";

const props = defineProps<{
    isEditing?: boolean;
    isSubmitting?: boolean;
}>();

const emit = defineEmits<{
    (e: "submit", value: SatelliteSiteFormValues): void;
    (e: "cancel"): void;
}>();

const model = defineModel<SatelliteSiteFormValues>({ required: true });

const rows = computed<BuilderRowConfig[]>(() => [
    {
        columns: [
            {
                name: "name",
                type: "text",
                label: "Site name",
                hint: "Internal label for admins. Example: Partner Blog or Agency Landing Page.",
                props: { placeholder: "Partner Blog" },
            },
        ],
    },
    {
        columns: [
            {
                name: "website_url",
                type: "text",
                label: "Website URL",
                hint: "The public origin allowed to submit contact leads.",
                props: { placeholder: "https://partner.example.com" },
            },
        ],
    },
    {
        columns: [
            {
                name: "is_active",
                type: "checkbox",
                label: "Status",
                hint: "Inactive sites keep their record but reject new submissions.",
                props: {
                    label: "Site is active and can submit contact leads",
                },
            },
        ],
    },
    {
        columns: [
            {
                name: "use_custom_access_key",
                type: "checkbox",
                label: "Access key",
                hint: props.isEditing
                    ? "Leave unchecked to keep the current key. Check and enter a value only when rotating manually."
                    : "Leave unchecked to auto-generate a secure key on create.",
                props: {
                    label: props.isEditing ? "Set a custom access key" : "Provide my own access key",
                },
            },
        ],
    },
    ...(model.value.use_custom_access_key
        ? [{
            columns: [{
                name: "access_key",
                type: "text" as const,
                label: "Custom access key",
                hint: "Minimum 8 characters. Store it securely — it authenticates the satellite site.",
                props: {
                    placeholder: "sec_your_custom_key",
                    type: "password",
                },
            }],
        }]
        : []),
]);

const formRef = useTemplateRef("formRef");

defineExpose({
    resetForm: () => formRef.value?.resetForm(),
    validate: (scrollToFirstError?: boolean) => formRef.value?.validate(scrollToFirstError),
});

function onSubmit(values: SatelliteSiteFormValues) {
    emit("submit", values);
}

function onCancel() {
    emit("cancel");
}
</script>

<template>
    <UiFormBuilder
        ref="formRef"
        v-model="model"
        :schema="satelliteSiteFormSchema"
        :rows="rows"
        scroll-to-first-error
        @submit="onSubmit"
    >
        <template #actions>
            <div class="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
                <UiButton variant="outline" label="Cancel" :disabled="props.isSubmitting" @click="onCancel" />
                <UiButton
                    type="submit"
                    variant="primary"
                    :label="props.isEditing ? 'Save Changes' : 'Create Site'"
                    :disabled="props.isSubmitting"
                />
            </div>
        </template>
    </UiFormBuilder>
</template>
